<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="reservationPageTitle"
    :on-close="doClose"
    :context-menu="reservationContextMenu"
    :lock-timer="lockTimerOverride ? lockTimerOverride : lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="reservationPageBarButtons"
  >
    <div
      v-if="isNewItem && reservationTypes.length > 1"
      class="ft-negmt-2"
    >
      <v-tabs
        ref="damntabs"
        v-model="selectedReservationTypeIndex"
        :light="!$ftAppIsDark"
        :dark="$ftAppIsDark"
        :color="$ftStyle.pageBarTabs"
        :disabled="isFormDisabled || !!itemData.cancelled"
        show-arrows
      >
        <v-tabs-slider :color="$ftStyle.pageBarTabsSlider" />
        <v-tab
          v-for="(tab, i) in reservationTypes"
          :key="i"
        >
          {{ tab.name }}
        </v-tab>
      </v-tabs>
      <v-divider />
    </div>

    <!-- MEMBER PREFERENCES DIALOG -->
    <member-preferences
      v-model="selectedMemberPreferencesId"
      @change="onMemberPrefsEdit($event)"
    />

    <!-- TABLE NUMBER USAGE DIALOG -->
    <v-dialog
      v-model="showTableNumberUsageDialog"
      :max-width="800"
      scrollable
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            Table Number Usage
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-data-table
            :mobile-breakpoint="0"
            hide-default-footer
            :loading="isDataLoading"
            :no-data-text="tableNumberUsageNoResults"
            :items-per-page="-1"
            :headers="[
              {
                text: 'Time',
                align: 'left',
                sortable: false,
                value: 'time'
              },
              {
                text: 'Table Numbers',
                align: 'left',
                sortable: false,
                value: 'tableNumbers'
              }
            ]"
            :items="tableNumberUsage"
          >
            <template
              slot="items"
              slot-scope="props"
            >
              <td>{{ getTimeDisplay(props.item.time) }}</td>
              <td>
                <template v-for="(table, i) in props.item.tableNumbers">
                  <span
                    v-for="(t, i2) in splitTableNumbers(table)"
                    :key="i + '-' + i2"
                    :class="tableNumberUsageClass(t)"
                  >{{ t }}</span>
                </template>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="blue--text darken-1"
            text
            @click="showTableNumberUsageDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ADD MULTIPLE RESERVEES DIALOG -->
    <v-dialog
      v-model="showAddMultipleDialog"
      :max-width="300"
      scrollable
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            Add Multiple Reservees
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            ref="addMultipleInput"
            v-model="addMultipleCount"
            label="Number of Reservees To Add"
            type="number"
            :disabled="isFormDisabled || !!itemData.cancelled"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="showAddMultipleDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            text
            @click="addMultiple"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CANCEL RESERVATION DIALOG -->
    <v-dialog
      v-model="showCancelReservationDialog"
      :max-width="300"
      scrollable
    >
      <v-card>
        <v-card-title style="flex-wrap: nowrap">
          <v-icon
            class="pr-2"
            color="error"
          >
            mdi-cancel
          </v-icon>
          <span class="headline">Cancel Reservation</span>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div>Are you sure you want to cancel the entire reservation?</div>
          <v-boolean
            v-if="forEvent"
            v-model="itemData.cancelFeeExempt"
            label="Cancellation Fee Exempt"
            :error-messages="getPathErrors(jsonPath, 'cancelFeeExempt')"
            :field-errors="getPathErrors(jsonPath, 'cancelFeeExempt')"
            :disabled="isFormDisabled"
          />
          <v-autocomplete
            v-model="itemData.cancellationReasonId"
            label="Cancellation Reason"
            auto
            :items="cancellationReasons"
            :error-messages="getPathErrors(jsonPath, 'cancellationReasonId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'cancellationReasonId')"
          />
          <v-text-field
            label="Cancellation Notes"
            :value="itemData.cancellationNotes"
            :error-messages="getPathErrors(jsonPath, 'cancellationNotes')"
            :disabled="isFormDisabled"
            @change="v => itemData.cancellationNotes = v && v.trim()"
            @input="clearPathErrors(jsonPath, 'cancellationNotes')"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="red--text darken-1"
            text
            @click="abortReservationCancellation"
          >
            No
          </v-btn>
          <v-btn
            class="blue--text darken-1"
            text
            @click="cancelReservation"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- START OF FORM FIELDS -->


    <!-- RESERVATION CANCELLATION -->
    <expansion-card
      v-if="itemData.cancelled"
      icon="mdi-cancel"
    >
      <span slot="title">Cancellation Details</span>
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.cancellationReasonId"
            label="Cancellation Reason"
            auto
            :items="cancellationReasons"
            :error-messages="getPathErrors(jsonPath, 'cancellationReasonId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'cancellationReasonId')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            label="Cancellation Notes"
            :value="itemData.cancellationNotes"
            :error-messages="getPathErrors(jsonPath, 'cancellationNotes')"
            :disabled="isFormDisabled"
            @change="v => itemData.cancellationNotes = v && v.trim()"
            @input="clearPathErrors(jsonPath, 'cancellationNotes')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-boolean
            v-if="forEvent"
            v-model="itemData.cancelFeeExempt"
            label="Cancellation Fee Exempt"
            :error-messages="getPathErrors(jsonPath, 'cancelFeeExempt')"
            :field-errors="getPathErrors(jsonPath, 'cancelFeeExempt')"
            :disabled="isFormDisabled"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <!-- RESERVATION CONFIRMATION -->
    <expansion-card
      v-if="showConfirmationDetails"
      :icon="itemData.walkIn ? 'mdi-walk' : 'mdi-phone'"
    >
      <span slot="title">Confirmation Details</span>
      <span slot="title-right">
        <v-layout wrap>
          <v-flex style="width: 150px">
            <v-boolean
              v-if="!$vuetify.breakpoint.xs"
              v-model="calledBackSwitch"
              style="margin-top: 2px"
              hide-details
              :label="originalCalledAt ? 'Called Again' : 'Called Back'"
              :error-messages="getPathErrors(jsonPath, 'calledBackAt')"
              :field-errors="getPathErrors(jsonPath, 'calledBackAt')"
              :disabled="isFormDisabled || !!itemData.cancelled"
            />
          </v-flex>
          <v-flex
            v-if="itemData.calledBackAt"
            class="caption mr-3"
          >


            <div><strong>Last Called:</strong> {{ getDateTimeDisplay(itemData.calledBackAt) }}</div>
            <div style="font-size: 90%"><strong>By:</strong> {{ itemData.calledBackBy || $ftUser.name }}</div>
          </v-flex>
        </v-layout>
      </span>
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.confirmationStatusId"
            label="Confirmation Status"
            auto
            :items="confirmationStatuses"
            :error-messages="getPathErrors(jsonPath, 'confirmationStatusId')"
            :disabled="isFormDisabled || !!itemData.cancelled"
            clearable
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'confirmationStatusId')"
            @click:clear="$nextTick(() => itemData.confirmationStatusId = null)"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-textarea
            v-model.trim="itemData.confirmationNotes"
            label="Confirmation Notes"
            :error-messages="getPathErrors(jsonPath, 'confirmationNotes')"
            :disabled="isFormDisabled || !!itemData.cancelled"
            auto-grow
            :rows="1"
            @input="clearPathErrors(jsonPath, 'confirmationNotes')"
          />
        </v-flex>
      </v-layout>
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-boolean
            v-if="$vuetify.breakpoint.xs"
            v-model="calledBackSwitch"
            :label="originalCalledAt ? 'Called Again' : 'Called Back'"
            :error-messages="getPathErrors(jsonPath, 'calledBackAt')"
            :field-errors="getPathErrors(jsonPath, 'calledBackAt')"
            :disabled="isFormDisabled || !!itemData.cancelled"
          />
        </v-flex>
      </v-layout>
    </expansion-card>


    <v-layout wrap>
      <v-flex class="md12 lg6">
        <expansion-card icon="mdi-calendar">
          <span slot="title">Location / Time</span>
          <v-layout wrap>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- DINING DATE -->
              <date-picker
                v-if="!forEvent"
                v-model.trim="itemData.date"
                label="Date"
                style="flex: 1 1;"
                :min="minimumDiningDate"
                :max="maximumDiningDate"
                :allowed-dates="allowedPickerDates"
                :rules="rules().required()"
                :error-messages="getPathErrors(jsonPath, 'date')"
                :disabled="isFormDisabled || !!itemData.cancelled"
                :color="selectedDateIsInPast ? 'warning' : undefined"
                :input-class="{'ft-warning-input': selectedDateIsInPast}"
                :hint="selectedDateIsInPastMessage"
                :persistent-hint="selectedDateIsInPast"
                :events="dateHasBlocker"
                :event-color="dateColor"
                required
                @input="clearPathErrors(jsonPath, 'date')"
              />
              <!-- EVENT SELECTION -->
              <v-autocomplete
                v-else
                v-model="itemData.eventId"
                prepend-icon="mdi-calendar"
                label="Event"
                :items="eventList"
                :rules="rules().required()"
                :color="selectedDateIsInPast || (selectedEvent && selectedEvent.cancelled) ? 'warning' : undefined"
                :class="{'ft-warning-input': selectedDateIsInPast || (selectedEvent && selectedEvent.cancelled)}"
                :hint="selectedEventDateTime"
                :persistent-hint="!!itemData.eventId"
                :disabled="isFormDisabled || !!itemData.cancelled"
                :error-messages="getPathErrors(jsonPath, 'eventId').concat(...(!itemData.cancelled && selectedEvent && selectedEvent.cancelled ? ['Event is Canceled - ' + formatEventDateTime(selectedEvent)] : []))"
                item-text="name"
                item-value="id"
                required
              >
                <template
                  slot="item"
                  slot-scope="data"
                >
                  <v-list-item-content
                    v-if="(typeof data.item !== 'object')"
                    v-text="data.item"
                  />
                  <v-list-item-content v-else>
                    <v-list-item-title :class="{'ft-cancelled-event-option': data.item.cancelled}">
                      {{ data.item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ formatEventDateTime(data.item) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-flex>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- LOCATION SELECTION -->
              <v-autocomplete
                v-model="itemData.locationId"
                prepend-icon="mdi-map-marker"
                clearable
                label="Location"
                :items="availLocations"
                :max-height="400"
                :rules="rules().required()"
                required
                auto
                :error-messages="getPathErrors(jsonPath, 'locationId')"
                :disabled="disabledWithoutDate || !!itemData.cancelled"
                item-text="name"
                item-value="id"
                @input="clearPathErrors(jsonPath, 'locationId')"
                @click:clear="$nextTick(() => itemData.locationId = null)"
              />
            </v-flex>
          </v-layout>

          <v-layout wrap>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- PARTY SIZE -->
              <v-autocomplete
                v-model="partySize"
                prepend-icon="mdi-account-multiple"
                :color="partySize > maxPartySize ? 'warning' : undefined"
                :class="{'ft-warning-input': partySize > maxPartySize}"
                label="Party Size"
                :hint="overPartySizeMessage"
                :persistent-hint="partySize > maxPartySize"
                :items="partySizeSelect"
                :rules="rules().required()"
                auto
                :disabled="isFormDisabled || !!itemData.cancelled"
                item-text="value"
                item-value="id"
                required
              />
            </v-flex>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- SEATING TIME -->
              <v-autocomplete
                v-model="selectedSeatingTime"
                :prepend-icon="firstValidSeatingTime ? 'mdi-clock-outline' : 'mdi-clock-alert'"
                label="Time"
                auto
                :menu-props="{ auto: true }"
                :items="seatingTimesList"
                :return-object="true"
                :rules="rules().required()"
                required
                clearable
                :error="selectedSeatingTime && selectedSeatingTime.disabled"
                :error-messages="getPathErrors(jsonPath, 'time')"
                :disabled="disabledWithoutDate || !!itemData.cancelled"
                item-text="uname"
                :color="overSeatingTimeMessage ? 'warning' : undefined"
                :class="{'ft-warning-input': !!overSeatingTimeMessage}"
                :hint="overSeatingTimeMessage"
                :persistent-hint="!!overSeatingTimeMessage"
                @input="clearPathErrors(jsonPath, 'time')"
                @click:clear="$nextTick(() => selectedSeatingTime = null)"
              >
                <template
                  slot="item"
                  slot-scope="data"
                >
                  <v-list-item-avatar class="ft-time-avatar ma-0 pa-0">
                    <v-chip><strong>{{ data.item.displayTime }}</strong></v-chip>
                  </v-list-item-avatar>
                  <!-- don't know why eslint fails this.  it works fine -->
                  <v-list-item-content class="pl-3">
                    <v-list-item-title>
                      <span v-if="data.item.label">{{ data.item.label }}</span>
                      <span
                        v-if="memberRestrictionsMap[data.item.memberRestrictionId]"
                        class="ft-time-restriction warning--text"
                      >
                        ({{ memberRestrictionsMap[data.item.memberRestrictionId].name }})
                      </span>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="data.item.blockReason && !data.item.allowedForStaff">
                      <template>
                        <span class="pr-2 red--text darken-1">
                          {{ data.item.blockReason }}
                        </span>
                      </template>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else>
                      <span
                        v-if="data.item.blockReason"
                        class="pr-2 red--text darken-1"
                      >
                        {{ data.item.blockReason }}
                      </span>
                      <span
                        v-else-if="!data.item.allowMemberMade"
                        class="pr-2 red--text darken-1"
                      >
                        Members Blocked
                      </span>
                      <template v-for="(count, i) in data.item.seatingCounts">
                        <span
                          :key="i"
                          class="pr-2"
                          :class="{'red--text darken-1': count.periodCovers >= count.max }"
                        >
                          <span>({{ count.name }}</span>:
                          <span>{{ count.periodCovers + (selectedSeatingTime === data.item ? partySize : 0) }}</span>
                          of {{ count.max }})</span>
                      </template>
                      <span
                        class="pr-2"
                        :class="{'red--text darken-1': periodCountsByPartySize(data.item, partySize).availTables - (selectedSeatingTime !== data.item ? 0 : usedTableQuantity) < 0 }"
                      >
                        <span>(Tables</span>:
                        <span>{{ periodCountsByPartySize(data.item, partySize).usedTables + (selectedSeatingTime !== data.item ? 0 : usedTableQuantity) }}</span>
                        of {{ periodCountsByPartySize(data.item, partySize).totalTables }})</span>
                      <span
                        v-if="data.item.waitlistCovers"
                      >
                        <v-icon
                          small
                          color="warning"
                        >mdi-pause-octagon</v-icon>{{ data.item.waitlistCovers }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template slot="no-data">
                  <v-list>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>{{ noTimesMessage }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </template>
              </v-autocomplete>
            </v-flex>
          </v-layout>
        </expansion-card>
      </v-flex>
      <v-flex class="md12 lg6">
        <expansion-card icon="mdi-message-settings-variant">
          <span slot="title">Preferences</span>

          <v-layout wrap>
            <v-flex
              v-if="itemData.discreteCovers && partySize > 1"
              class="xs12 px-3"
              :class="{'sm6': !hasTableAndCheckNumber, 'sm3': hasTableAndCheckNumber}"
            >
              <!-- NUMBER OF CHECKS -->
              <v-autocomplete
                v-model="itemData.numberOfChecks"
                prepend-icon="mdi-cards-outline"
                label="Number of Checks"
                class="ft-expand-label-input"
                :max-height="400"
                :items="numberOfChecksSelector"
                :error-messages="getPathErrors(jsonPath, 'numberOfChecks')"
                required
                auto
                :disabled="isFormDisabled || !!itemData.cancelled"
                item-text="value"
                item-value="value"
                @input="setCheckNumbers"
              />
            </v-flex>
            <!-- TABLE NUMBER -->
            <v-flex
              v-if="promptForTableNumber"
              class="xs12 px-3"
              :class="{'sm6': !hasTableAndCheckNumber, 'sm3': hasTableAndCheckNumber}"
            >
              <v-text-field
                v-model.trim="itemData.tableNumber"
                label="Table Number"
                class="ft-expand-label-input"
                prepend-icon="fti-table"
                :append-icon="allowShowTableNumberUse ? 'mdi-format-list-checks' : undefined"
                :error-messages="getPathErrors(jsonPath, 'tableNumber')"
                :disabled="isFormDisabled"
                @input="clearPathErrors(jsonPath, 'tableNumber')"
                @click:append="showTableNumberUsage"
              />
            </v-flex>
            <v-flex class="xs12 px-3 sm6">
              <!-- OCCASION SELECTION -->
              <v-autocomplete
                v-model="itemData.occasionId"
                prepend-icon="mdi-gift"
                label="Occasion"
                clearable
                :max-height="400"
                :items="occasions"
                auto
                :disabled="isFormDisabled || !!itemData.cancelled"
                item-value="id"
                item-text="name"
                @click:clear="$nextTick(() => itemData.occasionId = null)"
              />
            </v-flex>
          </v-layout>

          <v-layout wrap>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- SPECIAL REQUEST - MEMBER VISIBLE -->
              <v-textarea
                prepend-icon="mdi-message-text"
                label="Member Special Requests"
                :value="itemData.memberSpecialRequests"
                :rows="1"
                auto-grow
                :error-messages="getPathErrors(jsonPath, 'memberSpecialRequests')"
                :disabled="isFormDisabled || !!itemData.cancelled"
                hint="Visible to staff and members"
                persistent-hint
                @change="v => itemData.memberSpecialRequests = v && v.trim()"
                @input="clearPathErrors(jsonPath, 'memberSpecialRequests')"
              />
            </v-flex>
            <v-flex class="xs12 sm6 md6 px-3">
              <!-- SPECIAL REQUEST - STAFF VISIBLE -->
              <v-textarea
                prepend-icon="mdi-message-outline"
                label="Hidden Special Requests"
                :value="itemData.specialRequests"
                :rows="1"
                auto-grow
                :error-messages="getPathErrors(jsonPath, 'specialRequests')"
                :disabled="isFormDisabled || !!itemData.cancelled"
                hint="Visible to staff only"
                persistent-hint
                @change="v => itemData.specialRequests = v && v.trim()"
                @input="clearPathErrors(jsonPath, 'specialRequests')"
              />
            </v-flex>
          </v-layout>
        </expansion-card>
      </v-flex>
    </v-layout>

    <!-- RESERVATION RESTRICTIONS -->
    <expansion-card
      v-if="showRestrictionPanel"
      icon="mdi-format-list-checks"
    >
      <span slot="title">Restrictions / Style</span>
      <v-layout wrap>
        <!-- <v-flex
          v-if="reservationNoChildren"
          class="xs12 sm6 md6 lg3 px-3"
        >
          <v-text-field
            prepend-icon="mdi-cancel"
            :value="reservationNoChildren"
            :readonly="true"
          />
        </v-flex> -->
        <v-flex
          v-if="reservationDressCode"
          class="xs12 sm6 md6 lg3 px-3"
        >
          <v-text-field
            prepend-icon="mdi-tie"
            label="Dress Code"
            :value="reservationDressCode.name"
            :readonly="true"
          />
        </v-flex>
        <v-flex
          v-if="reservationMusicalStyle"
          class="xs12 sm6 md6 lg3 px-3"
        >
          <v-text-field
            prepend-icon="mdi-music"
            label="Muscial Style"
            :value="reservationMusicalStyle.name"
            :readonly="true"
          />
        </v-flex>
        <v-flex
          v-if="reservationTheme"
          class="xs12 sm6 md6 lg3 px-3"
        >
          <v-text-field
            prepend-icon="fa-theater-masks"
            label="Theme"
            :value="reservationTheme"
            :readonly="true"
          />
        </v-flex>
        <v-flex
          v-if="reservationExtras"
          class="xs12 sm6 md6 lg3 px-3"
        >
          <v-text-field
            prepend-icon="mdi-message"
            label="Extras"
            :value="reservationExtras"
            :readonly="true"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <!-- QUESTIONS / ANSWERS FOR ENTIRE RESERVATION -->
    <expansion-card
      v-if="itemData.answers && itemData.answers[0]"
      icon="mdi-help-circle-outline"
    >
      <span slot="title">Questions</span>
      <v-layout wrap>
        <v-flex
          v-for="(answer, i) in itemData.answers.filter(a => isActiveAnswer(a))"
          :key="answer.q.id"
          class="px-3 sm12 md6 lg4 ft-expand-label-input"
        >
          <v-text-field
            v-if="!answer.q.values.find(v => !v.disabled)"
            v-model="answer.value"
            :label="answer.q.question"
            :required="answer.q.required"
            :rules="answer.q.required ? rules().required() : []"
            :disabled="isFormDisabled || !!itemData.cancelled"
            :error-messages="getPathErrors(jsonPath, 'answers', i, 'question').concat(getPathErrors(jsonPath, 'answers', i, 'value'))"
            clearable
            @input="clearPathErrors(jsonPath, 'answers', i, 'question'); clearPathErrors(jsonPath, 'answers', i, 'value')"
          />
          <v-autocomplete
            v-else
            v-model="answer.value"
            :label="answer.q.question"
            auto
            :items="activeValues(answer)"
            :required="answer.q.required"
            :rules="answer.q.required ? rules().required() : []"
            :disabled="isFormDisabled || !!itemData.cancelled"
            item-text="value"
            item-value="value"
            :error-messages="getPathErrors(jsonPath, 'answers', i, 'question').concat(getPathErrors(jsonPath, 'answers', i, 'value'))"
            clearable
            @input="clearPathErrors(jsonPath, 'answers', i, 'question'); clearPathErrors(jsonPath, 'answers', i, 'value')"
            @click:clear="$nextTick(() => answer.value = null)"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-layout wrap>
      <v-flex
        v-if="selectedTableConfiguration && selectedTableConfiguration.tables[0]"
        class="md12 lg5 order-lg2"
      >
        <!-- TABLE ASSIGNMENTS -->
        <table-assignments
          v-model="itemData.tableAssignments"
          :json-path="buildJsonPath('tableAssignments')"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :move-path-errors="movePathErrors"
          :reload-location-tables="loadLocationTables"
          :disabled="isFormDisabled || !!itemData.cancelled"
          :hide-default-footer="!!itemData.cancelled"
          :table-config="selectedTableConfiguration"
          :period-count="periodCountsByPartySize(selectedSeatingTime || {}, partySize)"
          :party-size="partySize"
          :require-table-selection="requireTableSelection"
          @add="resetItemFieldErrorLookup"
          @remove="resetItemFieldErrorLookup"
          @update="getPlaceHolder"
        />
      </v-flex>
      <v-flex
        class="md12 ft-no-overflow"
        :class="{'lg7': selectedTableConfiguration && selectedTableConfiguration.tables[0]}"
      >
        <v-alert
          v-if="enablePOSChargeIntegration && itemData.details && itemData.details.some(d => d.billed)"
          type="info"
          outlined
          dense
          class="mb-3"
        >
          <div class="d-flex align-start">
            <v-icon
              small
              class="mr-2 mt-1"
              color="info"
            >
              mdi-receipt
            </v-icon>

            <div>
              <div class="text-body-2 font-weight-medium">
                Some reservees have been sent to POS
              </div>

              <div class="text-caption text--secondary mt-1">
                These entries are already in POS. Updates here will not automatically sync to existing POS tickets.
              </div>
            </div>
          </div>
        </v-alert>
        <!-- RESERVEES -->
        <detail-editor-table
          v-if="(!itemData.cancelled || (selectedTableConfiguration && selectedTableConfiguration.tables[0] && $vuetify.breakpoint.lg)) && itemData.details"
          v-model="filteredItemDetails"
          type="Reservee"
          icon="mdi-account-group"
          :json-path="buildJsonPath('details')"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :move-path-errors="movePathErrors"
          :disabled="isFormDisabled"
          :item-default="tbdDetailDefault"
          :warnings="detailWarnings"
          :sortable="itemData.discreteCovers"
          delete-icon="mdi-account-off"
          :delete-action="moveDetailToCancelled"
          :disable-delete="!itemData.discreteCovers || !itemData.details || !itemData.details[1] || !!itemData.cancelled"
          :delete-confirm-text="deleteReserveeConfirmText"
          :hide-actions="!itemData.discreteCovers || !!itemData.cancelled"
          :headers="[
            ...(itemData.discreteCovers ? [headerDragSort()] : []),
            headerEditor({label: 'Type', field: 'guestTypeId', editor: {type: 'slot'}, thin: true}),
            headerEditor({label: 'Reservee', field: 'name', editor: {type: 'slot'}}),
            ...((selectedEvent && selectedEvent.costs && selectedEvent.costs[0]) ? [headerEditor({label: 'Price Category', field: 'eventCostId', editor: {type: 'slot'}})] : []),
            ...(( !itemData.discreteCovers) ? [] : [headerEditor({label: 'Check#', field: 'checkNumber', editor: {type: 'slot'}, thin: true})]),
            ...(( enablePOSChargeIntegration && selectedEvent && selectedEvent.costs && selectedEvent.costs[0]) ? [headerEditor({label: 'Bill To', field: 'billTo', editor: {type: 'slot'}, thin: true})] : []),
            ...(enableNoShow ? [headerEditor({label: 'No-Show', field: 'show', editor: {type: 'slot'}, thin: true})] : [])
          ]"
          @dragstart="hideLastActive"
          @add="onReserveeAdd"
          @delete="generateAnswers"
        >
          <!-- PARTY SIZE -->
          <!--
        <v-autocomplete
            class="pt-0 mr-2 ft-left-label"
            slot="title-right"
            :hide-details="!overPartySizeMessage"
            :color="partySize > maxPartySize ? 'warning' : undefined"
            :class="{'ft-warning-input': partySize > maxPartySize}"
            label="Party Size"
            :hint="overPartySizeMessage"
            :persistent-hint="partySize > maxPartySize"
            :items="partySizeSelect"
            v-model="partySize"
            :rules="rules().required()"
            auto
            :disabled="isFormDisabled || !!itemData.cancelled"
            item-text="value"
            item-value="id"
            required />
            -->
          <v-boolean
            v-if="!discreteCoversMatchSeating"
            slot="heading"
            v-model="itemData.discreteCovers"
            label="Details for Each Reservee"
            :hint="discreteCoversWarning"
            :persistent-hint="!discreteCoversMatchSeating"
            :error-messages="getPathErrors(jsonPath, 'discreteCovers')"
            :field-errors="getPathErrors(jsonPath, 'discreteCovers')"
            :disabled="isFormDisabled"
            :warning="!discreteCoversMatchSeating"
            :hide-details="discreteCoversMatchSeating && !getPathErrors(jsonPath, 'discreteCovers')._hasErrors"
          />

          <template
            slot="guestTypeId"
            slot-scope="props"
          >
            <v-select
              v-model="props.item.guestTypeId"
              auto
              placeholder="Type"
              :items="modGuestTypes"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              item-text="name"
              item-value="id"
              style="min-width: 100px"
              @input="props.errorMessages._clear(); typeSelected.call(_self, props)"
            >
              <template
                slot="item"
                slot-scope="data"
              >
                <span :class="{'warning--text': blockedGuestTypes[data.item.id]}">{{ data.item.name }}</span>
              </template>
              <template
                slot="selection"
                slot-scope="data"
              >
                <div :class="{'warning--text': blockedGuestTypes[data.item.id], 'v-select__selection': true, 'v-select__selection--comma': true}">
                  {{ data.item.name }}
                </div>
              </template>
            </v-select>
          </template>
          <template
            slot="checkNumber"
            slot-scope="props"
          >
            <v-select
              v-model="props.item.checkNumber"
              auto
              :items="checkNumberSelector"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              item-text="value"
              item-value="value"
              @input="val => onCheckNumberChange(props.item, val)"
            />
          </template>
          <template
            v-if="enablePOSChargeIntegration"
            slot="billTo"
            slot-scope="props"
          >
            <v-checkbox
              :key="`${props.item.id || props.row}_${props.item.checkNumber}_${props.item.billTo}`"
              :input-value="props.item.billTo"
              :disabled="isFormDisabled || isLastBillTo(props.item)"
              @change="val => onBillToChange(props.item, val)"
            />
          </template>
          <template
            slot="name"
            slot-scope="props"
          >
            <member-select
              v-if="!props.item.guestTypeId"
              :ref="'reserveeSelect_' + props.row"
              :error-handler="apiResourceError"
              :used-member-ids="selectedMemberIds"
              :disabled="isFormDisabled"
              :value="props.item.memberId"
              return-object
              placeholder="Select Member"
              :error-messages="props.errorMessages"
              :preload="props.item.member && {
                id: props.item.memberId,
                nameFull: props.item.name,
                nameFirst: props.item.member.nameFirst,
                nameLast: props.item.member.nameLast,
                email: props.item.member.email,
                email2: props.item.member.email2,
                emailOpt: props.item.member.emailOpt,
                emailOpt2: props.item.member.emailOpt2,
                phone1: props.item.member.phone1,
                phone2: props.item.member.phone2,
                username: props.item.member.username,
                memNum: props.item.member.memNum,
                gender: props.item.member.gender,
                mShip: props.item.member.mShip,
                mType: props.item.member.mType,
                birth: props.item.member.birth,
                foodPreference: props.item.member.foodPreference,
                diningSpecialRequests: props.item.member.diningSpecialRequests,
                allergyInformation: props.item.member.allergyInformation,
                activePhoto: props.item.member.activePhoto
              }"
              :filters="{group: memberGroups, mType: memberTypes, mShip: membershipTypes}"
              enable-info-select
              :hint="props.item.memberId ? (showReserveeMemNum ? '(' + (showUsername ? props.item.username : props.item.memberNumber) + ') ' : '') + (props.item.notificationRcpts || []).join(', ') : ''"
              :persistent-hint="props.item.memberId && (showReserveeMemNum || !!(props.item.notificationRcpts || [])[0])"
              @change="memberSelected.call(_self, props, $event); props.errorMessages._clear()"
              @focus="memberSelectFocus"
              @filter-change="lastMemberFilter = $event"
              @info-select="openMemberPreferences"
            />
            <guest-select
              v-else
              :ref="'reserveeSelect_' + props.row"
              v-model="props.item"
              :error-handler="apiResourceError"
              :disabled="isFormDisabled"
              :guest-types="guestTypes"
              :error-messages="props.errorMessages"
              @input="props.errorMessages._clear()"
              @keydown.native.enter="guestSelected.call(_self, props)"
            />
            <div
              v-if="enablePOSChargeIntegration && props.item.billed"
              class="d-flex align-center mt-1 ft-muted-text"
            >
              <v-icon
                small
                class="mr-1"
              >
                mdi-receipt
              </v-icon>
              <span>Sent to POS</span>
            </div>
            <v-flex
              v-for="(answer, i) in props.item.answers.filter(a => isActiveAnswer(a))"
              :key="(answer.q && answer.q.id) + '_' + props.items.guestTypeId"
            >
              <v-text-field
                v-if="!answer.q.values.find(v => !v.disabled)"
                v-model="answer.value"
                :label="answer.q && answer.q.question"
                :required="answer.q.required"
                :rules="answer.q.required ? rules().required() : []"
                :disabled="isFormDisabled"
                :error-messages="getPathErrors(props.jsonRowPath, 'answers', i, 'question').concat(getPathErrors(props.jsonRowPath, 'answers', i, 'value'))"
                clearable
                @input="clearPathErrors(props.jsonRowPath, 'answers', i, 'question'); clearPathErrors(props.jsonRowPath, 'answers', i, 'value')"
              />
              <v-autocomplete
                v-else
                :key="answer.q.id + '_' + props.items.guestTypeId"
                v-model="answer.value"
                :label="answer.q.question"
                auto
                :items="activeValues(answer)"
                :required="answer.q.required"
                :rules="answer.q.required ? rules().required() : []"
                clearable
                :disabled="isFormDisabled"
                :error-messages="getPathErrors(props.jsonRowPath, 'answers', i, 'question').concat(getPathErrors(props.jsonRowPath, 'answers', i, 'value'))"
                item-text="value"
                item-value="value"
                @input="clearPathErrors(props.jsonRowPath, 'answers', i, 'question'); clearPathErrors(props.jsonRowPath, 'answers', i, 'value')"
                @click:clear="$nextTick(() => answer.value = null)"
              />
            </v-flex>
          </template>
          <template
            slot="eventCostId"
            slot-scope="props"
          >
            <v-autocomplete
              v-model="props.item.eventCostId"
              auto
              placeholder="Select Price Category"
              :items="selectedEvent.costs"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              :rules="rules().required()"
              required
              item-text="name"
              item-value="id"
              @input="props.errorMessages._clear();"
            >
              <template
                slot="item"
                slot-scope="data"
              >
                <v-list-item-content class="pl-3">
                  <v-list-item-title><span>{{ data.item.name }}</span></v-list-item-title>
                  <v-list-item-subtitle>
                    <template>
                      <span>${{ forceCurrency(data.item.price) }}</span>
                      <span v-if="data.item.hidePrice">(Hidden From Member)</span>
                    </template>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>
          <template
            slot="show"
            slot-scope="props"
          >
            <v-select
              v-if="useNoShowSelect"
              v-model="noShowCount"
              auto
              :items="noShowSelectList"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              @input="props.errorMessages._clear();"
            />
            <v-checkbox
              v-else
              v-model="props.item.noShow"
              :false-value="false"
              :true-value="true"
              color="error"
            />
          </template>
        </detail-editor-table>
      </v-flex>
    </v-layout>

    <!-- CANCELLED RESERVEES -->
    <detail-editor-table
      v-if="itemData.cancelledDetails && itemData.cancelledDetails.length > 0"
      v-model="itemData.cancelledDetails"
      type="Canceled Reservee"
      icon="mdi-cancel"
      :json-path="buildJsonPath('cancelledDetails')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      :item-default="{}"
      :hide-default-footer="true"
      delete-icon="mdi-backup-restore"
      delete-confirm-text="Restore"
      prompt-icon-color="primary"
      :delete-action="moveCancelledToDetail"
      :disable-delete="!!itemData.cancelled"
      :headers="[
        headerEditor({label: 'Reservee', field: 'name', editor: {type: 'slot'}}),
        headerEditor({label: 'Reason', field: 'cancellationReasonId', editor: {type: 'slot'}}),
        headerEditor({label: 'Notes', field: 'cancellationNotes', editor: {type: 'slot'}}),
        headerEditor({label: 'Canceled', field: 'cancelled', editor: {type: 'slot'}}),
        headerEditor({label: 'Check #', field: 'checkNumber', editor: {type: 'slot'}}),
        ...(enablePOSChargeIntegration
          ? [headerEditor({ label: 'Bill To', field: 'billTo', editor: { type: 'slot' } })]
          : []),
        ...(forEvent ? [headerEditor({label: 'Fee Exempt', field: 'cancelFeeExempt', editor: {type: 'slot'}, thin: true})] : [])
      ]"
      @add="resetItemFieldErrorLookup"
    >
      <template
        slot="name"
        slot-scope="props"
      >
        <span v-if="props.item.memberId">{{ props.item.name }} ({{ props.item.memberNumber }})</span>
        <span v-else>{{ props.item.name }} ({{ guestTypeMap[props.item.guestTypeId].name }})</span>
        <div
          v-if="enablePOSChargeIntegration && props.item.billed"
          class="d-flex align-center mt-1 ft-muted-text"
        >
          <v-icon
            small
            class="mr-1"
          >
            mdi-receipt
          </v-icon>
          <span>Sent to POS</span>
        </div>
      </template>
      <template
        slot="cancellationReasonId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.cancellationReasonId"
          auto
          :items="cancellationReasons"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          @input="props.errorMessages._clear();"
        />
      </template>
      <template
        slot="cancellationNotes"
        slot-scope="props"
      >
        <v-text-field
          v-model.trim="props.item.cancellationNotes"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.errorMessages._clear();"
        />
      </template>
      <template
        slot="cancelled"
        slot-scope="props"
      >
        <div>{{ getDateTimeDisplay(props.item.cancelled) }}</div>
        <div>{{ props.item.cancelledBy }}</div>
      </template>
      <template
        slot="checkNumber"
        slot-scope="props"
      >
        <v-select
          v-model="props.item.checkNumber"
          auto
          :items="checkNumberSelector"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          item-text="value"
          item-value="value"
          @input="val => onCheckNumberChange(props.item, val)"
        />
      </template>
      <template
        v-if="enablePOSChargeIntegration"
        slot="billTo"
        slot-scope="props"
      >
        <v-checkbox
          :key="`${props.item.id || props.row}_${props.item.checkNumber}_${props.item.billTo}`"
          :input-value="props.item.billTo"
          :disabled="isFormDisabled || isLastBillTo(props.item)"
          @change="val => onBillToChange(props.item, val)"
        />
      </template>
      <template
        slot="cancelFeeExempt"
        slot-scope="props"
      >
        <v-boolean
          v-model="props.item.cancelFeeExempt"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.errorMessages._clear();"
        />
      </template>
    </detail-editor-table>


    <expansion-card>
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <!-- EMAIL SEND TYPE -->
          <v-autocomplete
            v-model="reservationNotificationMode"
            label="Notification Mode"
            :items="reservationNotificationModes"
            auto
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <!-- BLOCK MEMBER EDIT -->
          <v-boolean
            v-model="itemData.blockMemberEdit"
            auto-inline
            label="Block Member Edit Access"
            :error-messages="getPathErrors(jsonPath, 'blockMemberEdit')"
            :field-errors="getPathErrors(jsonPath, 'blockMemberEdit')"
            :disabled="isFormDisabled"
          />
        </v-flex>
      </v-layout>
    </expansion-card>
  </item-editor-dialog>
</template>

<script>
// TODO: Find and fix performance issues for large numbers of reservees
// import moment from 'moment'
import {itemEdit, withChildren, GuestSelect} from '@/ft-vue-core'
import floorPlanReservationMixins from './mixins/floorPlanReservationMixins'
import reservationCommonData from './mixins/reservationCommonData'
import reservationsItemData from './mixins/reservationsItemData'
import reservationsItemComputed from './mixins/reservationsItemComputed'
import reservationsItemProps from './mixins/reservationsItemProps'
import reservationsCommonMethods from './mixins/reservationsCommonMethods'
import reservationsItemMethods from './mixins/reservationsItemMethods'
import placeholder from './mixins/placeholder'
import questions from './mixins/questions'
import reservationsItemResources from './mixins/reservationsItemResources'
import reservationsItemWatchers from './mixins/reservationsItemWatchers'
import TableAssignments from '@/components/common/TableAssignments'

export default {
  name: 'ReservationsItem',
  components: {GuestSelect, TableAssignments},
  mixins: [
    itemEdit,
    withChildren,
    floorPlanReservationMixins,
    reservationCommonData,
    reservationsItemData,
    reservationsItemComputed,
    reservationsItemProps,
    reservationsCommonMethods,
    reservationsItemMethods,
    placeholder,
    questions,
    reservationsItemResources,
    reservationsItemWatchers
  ],
  computed: {
    enablePOSChargeIntegration () {
      return this.$ftCS?.DINING_ENABLE_POS_CHARGE_INTEGRATION
    }
  },
  watch: {
    'itemData.details': {
      handler () {
        this.$nextTick(() => {
          this.enforceSingleBillTo()
        })
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    onReserveeAdd (item) {
      this.generateAnswers(item)

      this.$nextTick(() => {
        this.enforceSingleBillTo()
      })
    },
    isLastBillTo (item) {
      const all = [
        ...this.itemData.details || [],
        ...this.itemData.cancelledDetails || []
      ]

      const group = all.filter(r => r.checkNumber === item.checkNumber)
      return group.filter(r => r.billTo).length === 1 && item.billTo
    },
    onBillToChange (item, val) {
      item.billTo = val
      this.enforceSingleBillTo(item.checkNumber, item)
    },
    onCheckNumberChange (item, newCheckNumber) {
      item.checkNumber = newCheckNumber
      this.enforceSingleBillTo(newCheckNumber, item)
    },
    enforceSingleBillTo (checkNumber, activeItem) {
      if (!this.enablePOSChargeIntegration) return
      const all = [
        ...this.itemData.details || [],
        ...this.itemData.cancelledDetails || []
      ]

      const checkNumbers = all.reduce((acc, r) => {
        const key = r.checkNumber
        if (key === null || key === undefined || key === '') return acc

        if (!acc[key]) acc[key] = []
        acc[key].push(r)
        return acc
      }, {})

      Object.values(checkNumbers).forEach(group => {
        if (!group.length) return

        const billToItems = group.filter(r => r.billTo)

        if (billToItems.length > 1) {
          const keeper =
            activeItem && billToItems.includes(activeItem)
              ? activeItem
              : billToItems[0]

          group.forEach(r => {
            this.$set(r, 'billTo', r === keeper)
          })

          return
        }

        // CASE 2: no billTo → initialize one
        if (billToItems.length === 0) {
          const fallback =
            activeItem && group.includes(activeItem)
              ? activeItem
              : group[0]

          if (fallback) {
            group.forEach(r => {
              this.$set(r, 'billTo', r === fallback)
            })
          }
        }
      })
    },
    afterSave (r) {
      if (this.addWalkIn) this.$emit('after-save', r)
    }
  }
}
</script>
<style>
  .ft-event-or-mp-icon {
    font-size: 16px;
    margin-right: 8px;
  }
  .ft-time-avatar {
    width: auto !important;
    margin-left: 15px;
  }
  .ft-time-avatar .chip .chip__content {
    width: 90px;
    text-align: center;
    justify-content: center;
  }
  .ft-darken-hover:hover {
    filter: brightness(95%);
  }
  .ft-reservation-id-icon+span {
    position: relative
  }
  .ft-reservation-id-icon {
    position: absolute
  }
  .ft-cancelled-item {
    text-decoration: line-through
  }
  .ft-expand-chip .chip__content {
    height: auto !important;
    min-height: 32px;
    white-space: normal !important;
  }
  .ft-member-orig td {
    background-color: rgba(255, 255, 0, 0.09);
  }
  .ft-time-restriction {
    margin-left: 10px;
    font-size: 95%;
  }

</style>
